/**
 * recursos.ts — Categoria "Recursos" (10 modelos premium)
 *
 * Benefícios e diferenciais montados com seção em camadas + cartões,
 * números e imagens do acervo compartilhado.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote A)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

/** Atalho para um cartão de benefício. */
function cartao(icone: string, titulo: string, texto: string, variante = 'solid') {
  return b('ui.card', { icon: icone, title: titulo, text: texto, variant: variante, radius: 'xl', shadow: 'sm', motion: 'fade-up' });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'recursos.tres-cartoes',
    category: 'recursos',
    label: 'Três benefícios em cartões',
    description: 'Título centralizado e três cartões com sombra suave.',
    preview: 'grid-3',
    outputs: WEB,
    order: 1,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', motion: 'fade-up' },
        [
          b('ui.title', { text: 'Por que escolher a nossa solução', size: 'lg', level: 'h2', align: 'center' }),
          cartao('⚡', 'Implantação rápida', 'Sua equipe usando em poucos dias, com acompanhamento.'),
          cartao('🔒', 'Dados protegidos', 'Acesso por perfil e histórico de tudo o que acontece.'),
          cartao('📈', 'Resultado visível', 'Indicadores em tempo real para decidir com segurança.'),
        ],
      ),
    ],
  },

  {
    key: 'recursos.seis-itens',
    category: 'recursos',
    label: 'Seis diferenciais',
    description: 'Grade com seis cartões contornados, ideal para listas completas.',
    preview: 'grid-3',
    outputs: WEB,
    order: 2,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', backgroundColor: '#f8fafc', motion: 'fade' },
        [
          b('ui.title', { text: 'Tudo o que você precisa em um só lugar', size: 'lg', level: 'h2', align: 'center' }),
          cartao('✓', 'Cadastro completo', 'Clientes, contratos e histórico juntos.', 'outline'),
          cartao('✓', 'Propostas em minutos', 'Modelos prontos e envio por link.', 'outline'),
          cartao('✓', 'Cobrança automática', 'Boleto, cartão e Pix sem digitar duas vezes.', 'outline'),
          cartao('✓', 'Atendimento unificado', 'Todos os canais na mesma tela.', 'outline'),
          cartao('✓', 'Automação de rotina', 'Tarefas e lembretes no tempo certo.', 'outline'),
          cartao('✓', 'Relatórios prontos', 'Acompanhe metas sem planilha.', 'outline'),
        ],
      ),
    ],
  },

  {
    key: 'recursos.escuro-brilho',
    category: 'recursos',
    label: 'Recursos em fundo escuro',
    description: 'Fundo escuro com brilho e cartões de vidro.',
    preview: 'grid-3',
    outputs: WEB,
    order: 3,
    build: () => [
      b(
        'section.layered',
        {
          paddingY: 'xl',
          align: 'center',
          maxWidth: '1120px',
          layout: 'bento',
          backgroundGradient: 'mesh-dark',
          glow: 'center',
          textColor: '#ffffff',
          motion: 'fade-up',
        },
        [
          b('ui.badge', { text: 'Recursos', variant: 'glass', align: 'center' }),
          b('ui.title', { text: 'Feito para operações exigentes', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' }),
          cartao('◆', 'Alta disponibilidade', 'Infraestrutura monitorada 24 horas.', 'glass'),
          cartao('◆', 'Integrações abertas', 'Converse com os sistemas que você já usa.', 'glass'),
          cartao('◆', 'Escala sem susto', 'Cresça sem trocar de ferramenta.', 'glass'),
        ],
      ),
    ],
  },

  {
    key: 'recursos.lado-imagem',
    category: 'recursos',
    label: 'Recursos ao lado da foto',
    description: 'Lista de benefícios à esquerda e foto do produto à direita.',
    preview: 'split-right',
    outputs: WEB,
    order: 4,
    build: () => [
      b(
        'section.layered',
        { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade' },
        [
          b('ui.title', { text: 'Menos cliques, mais resultado', size: 'lg', level: 'h2' }),
          cartao('1', 'Fluxo guiado', 'Cada etapa mostra o próximo passo.'),
          cartao('2', 'Alertas no tempo certo', 'Nada importante passa batido.'),
          b('ui.image', { src: FOTO.notebook, alt: 'Sistema em uso', ratio: 'landscape', radius: 'xl', shadow: 'lg', motion: 'scale' }),
        ],
      ),
    ],
  },

  {
    key: 'recursos.dois-destaques',
    category: 'recursos',
    label: 'Dois grandes destaques',
    description: 'Dois cartões largos, bons para dois pilares do serviço.',
    preview: 'grid-2',
    outputs: WEB,
    order: 5,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'center', maxWidth: '1000px', layout: 'bento', backgroundGradient: 'soft', motion: 'fade-up' },
        [
          b('ui.title', { text: 'Dois pilares que sustentam a entrega', size: 'lg', level: 'h2', align: 'center' }),
          cartao('🤝', 'Time dedicado', 'Um responsável que conhece a sua operação e responde rápido.'),
          cartao('🛠️', 'Processo testado', 'Método aplicado em centenas de empresas, adaptado à sua realidade.'),
        ],
      ),
    ],
  },

  {
    key: 'recursos.com-numeros',
    category: 'recursos',
    label: 'Benefícios com números',
    description: 'Mistura cartões de benefício com números de resultado.',
    preview: 'stats',
    outputs: WEB,
    order: 6,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', pattern: 'dots', motion: 'fade' },
        [
          b('ui.title', { text: 'O ganho aparece já no primeiro mês', size: 'lg', level: 'h2', align: 'center' }),
          b('ui.stat', { value: '3x', label: 'mais propostas enviadas', align: 'center' }),
          b('ui.stat', { value: '12h', label: 'economizadas por semana', align: 'center' }),
          cartao('✓', 'Sem curva longa', 'Treinamento incluído e material de apoio.'),
          cartao('✓', 'Suporte de gente', 'Atendimento por WhatsApp em horário comercial.'),
        ],
      ),
    ],
  },

  {
    key: 'recursos.lista-clara',
    category: 'recursos',
    label: 'Lista clara de entregas',
    description: 'Coluna única com itens objetivos e marcação de conferido.',
    preview: 'list',
    outputs: WEB,
    order: 7,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'left', maxWidth: '820px', motion: 'fade-up' },
        [
          b('ui.badge', { text: 'Está incluso', variant: 'soft' }),
          b('ui.title', { text: 'O que você recebe', size: 'md', level: 'h2' }),
          cartao('✓', 'Implantação assistida', 'Configuração inicial feita junto com a sua equipe.', 'outline'),
          cartao('✓', 'Migração dos dados', 'Trazemos seus cadastros do sistema atual.', 'outline'),
          cartao('✓', 'Treinamento gravado', 'Material disponível para novos colaboradores.', 'outline'),
          cartao('✓', 'Acompanhamento mensal', 'Reunião de resultados com o time responsável.', 'outline'),
        ],
      ),
    ],
  },

  {
    key: 'recursos.imagem-fundo',
    category: 'recursos',
    label: 'Recursos sobre foto',
    description: 'Foto de fundo escurecida com três cartões de vidro.',
    preview: 'grid-3',
    outputs: WEB,
    order: 8,
    build: () => [
      b(
        'section.layered',
        {
          backgroundImage: FOTO.industria,
          overlay: 'darker',
          paddingY: 'xl',
          align: 'center',
          layout: 'bento',
          textColor: '#ffffff',
          maxWidth: '1120px',
          motion: 'fade',
        },
        [
          b('ui.title', { text: 'Do chão de fábrica ao relatório', size: 'lg', level: 'h2', align: 'center' }),
          cartao('▣', 'Controle em tempo real', 'Cada etapa registrada no momento em que acontece.', 'glass'),
          cartao('▣', 'Rastreabilidade total', 'Histórico completo por lote, cliente e responsável.', 'glass'),
          cartao('▣', 'Menos perda', 'Alertas automáticos quando algo sai do padrão.', 'glass'),
        ],
      ),
    ],
  },

  {
    key: 'recursos.quatro-simples',
    category: 'recursos',
    label: 'Quatro recursos essenciais',
    description: 'Grade de quatro cartões limpos, sem excesso de cor.',
    preview: 'grid-4',
    outputs: WEB,
    order: 9,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', motion: 'fade-up' },
        [
          b('ui.title', { text: 'Simples de usar, completo por dentro', size: 'lg', level: 'h2', align: 'center' }),
          cartao('◔', 'Rápido', 'Abre em segundos, mesmo em conexão fraca.'),
          cartao('◑', 'Organizado', 'Cada informação no lugar certo.'),
          cartao('◕', 'Seguro', 'Permissões por perfil e registro de acessos.'),
          cartao('●', 'Acompanhado', 'Suporte próximo desde o primeiro dia.'),
        ],
      ),
    ],
  },

  {
    key: 'recursos.destaque-unico',
    category: 'recursos',
    label: 'Um recurso em destaque',
    description: 'Um único benefício com foto grande e chamada de ação.',
    preview: 'split-left',
    outputs: WEB,
    order: 10,
    build: () => [
      b(
        'section.layered',
        { layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1080px', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade' },
        [
          b('ui.image', { src: FOTO.dados, alt: 'Relatórios do sistema', ratio: 'landscape', radius: 'xl', shadow: 'lg' }),
          b('ui.badge', { text: 'Destaque', variant: 'solid' }),
          b('ui.title', { text: 'Relatórios que a diretoria entende', size: 'lg', level: 'h2' }),
          b('ui.paragraph', { text: 'Painéis prontos por área, com exportação em um clique e envio automático por e-mail.' }),
          b('ui.buttons', { items: 'Ver um exemplo|#contato|solid' }),
        ],
      ),
    ],
  },
];

registerPatterns(modelos);
